import { useMemo } from 'react';
import { Outlet, Navigate, useRoutes } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import Page404 from '../pages/404/Page404';
import SimpleLayout from '../layouts/simple';
import Status from '../pages/statuses/Status';
import TagsPage from '../pages/tags/TagsPage';
import RolesPage from '../pages/roles/RolesPage';
import FormsPage from '../pages/forms/FormsPage';
import LoginPage from '../pages/Login/loginPage';
import UsersPage from '../pages/users/UsersPage';
import DashboardLayout from '../layouts/dashboard';
import NewFormPage from '../pages/forms/NewFormPage';
import SettingsPage from '../pages/settings/Settings';
import ProfilePage from '../pages/profile/ProfilePage';
import TicketsPage from '../pages/tickets/TicketsPage';
import FirstLogin from '../pages/firstLogin/FirstLogin';
import DashboardApp from '../pages/dashboards/DashboardApp';
import GroupUsersPage from '../pages/groupUsers/GroupUsersPage';
import DepartmentsPage from '../pages/departments/DepartmentsPage';
import FormUnavailable from '../pages/ticketCreation/FormUnavailable';
import AssignmentsCalendar from '../pages/calendar/AssignmentsCalendar';
import CreateTicketPage from '../pages/ticketCreation/CreateTicketPage';
import TicketDetailsPage from '../pages/ticketDetails/TicketDetailsPage';
import ShareableFormsPage from '../pages/shareableForms/ShareableFormsPage';
import { ProtectedRoute } from '../components/protected-route/ProtectedRoute';
import TooManyRequestsPage from '../pages/ticketCreation/TooManyRequestsPage';
import FormSharableSubmitted from '../pages/ticketCreation/FormSharableSubmitted';
import ClientCredentialsPage from '../pages/clientCredentials/ClientCredentialsPage';

export default function Router() {
  const { isAuthenticated, firstLogin } = useAuth();

  const routes = useMemo(() => {
    if (!isAuthenticated) {
      return [
        { path: 'login', element: <LoginPage /> },
        { path: 'create-ticket', element: <CreateTicketPage /> },
        { path: 'newTicket', element: <CreateTicketPage /> },
        { path: 'form-sharable-submitted', element: <FormSharableSubmitted /> },
        { path: 'form-unavailable', element: <FormUnavailable /> },
        { path: 'too-many-requests', element: <TooManyRequestsPage /> },
        { path: '*', element: <Navigate to="/login" replace /> },
        { path: 'resetPassword', element: <FirstLogin /> },
      ];
    }

    return [
      {
        path: '/dashboard',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { element: <Navigate to="/dashboard/app" />, index: true },
          { path: 'app', element: <DashboardApp /> },
          {
            path: 'user',
            element: (
              <ProtectedRoute permission="USERS:UI_ACCESS">
                <UsersPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'departments',
            element: (
              <ProtectedRoute permission="DEPARTMENTS:UI_ACCESS">
                <DepartmentsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'forms',
            element: (
              <ProtectedRoute permission="FORMS:UI_ACCESS">
                <FormsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'NewForm',
            element: (
              <ProtectedRoute permission="FORMS:UI_ACCESS">
                <NewFormPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'tickets',
            element: (
              <ProtectedRoute permission="TICKETS:UI_ACCESS">
                <TicketsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'newTicket',
            element: (
              <ProtectedRoute permission="TICKETS:UI_ACCESS">
                <CreateTicketPage />
              </ProtectedRoute>
            ),
          },
          { path: 'profile', element: <ProfilePage /> },
          {
            path: 'settings',
            element: (
              <ProtectedRoute permission="SETTINGS:UI_ACCESS">
                <SettingsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'Status',
            element: (
              <ProtectedRoute permission="STATUSES:UI_ACCESS">
                <Status />
              </ProtectedRoute>
            ),
          },
          {
            path: 'tags',
            element: (
              <ProtectedRoute permission="ATTRIBUTES:UI_ACCESS">
                <TagsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'shareableForms',
            element: (
              <ProtectedRoute permission="SHAREABLE_FORMS:UI_ACCESS">
                <ShareableFormsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'clientCredentials',
            element: (
              <ProtectedRoute permission="CLIENT_CREDENTIALS:UI_ACCESS">
                <ClientCredentialsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'myGroups',
            element: (
              <ProtectedRoute permission="MY_GROUPS:UI_ACCESS">
                <GroupUsersPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'calendar',
            element: (
              <ProtectedRoute permission="CALENDAR:UI_ACCESS">
                <AssignmentsCalendar />
              </ProtectedRoute>
            ),
          },
          {
            path: 'roles',
            element: (
              <ProtectedRoute permission="ROLES:UI_ACCESS">
                <RolesPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: '/',
        element: <Outlet />,
        children: [
          { path: 'login', element: <Navigate to="/dashboard/app" replace /> },

          { path: 'firstLogin', element: <FirstLogin /> },

          {
            path: 'tickets/:ticketId',
            element: <TicketDetailsPage />,
          },
          { path: 'form-sharable-submitted', element: <FormSharableSubmitted /> },
          { path: 'form-unavailable', element: <FormUnavailable /> },
          { path: 'too-many-requests', element: <TooManyRequestsPage /> },
          { element: <Navigate to="/dashboard/app" />, index: true },
          { path: 'resetPassword', element: <FirstLogin /> },
        ],
      },
      {
        element: <SimpleLayout />,
        children: [
          { path: '404', element: <Page404 /> },
          { path: '*', element: <Navigate to="/404" /> },
        ],
      },
      { path: '*', element: <Navigate to="/404" replace /> },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, firstLogin]);

  return useRoutes(routes);
}
